import type { z } from 'zod';
import type { realtimeInputSchema } from './realtime';
import type { RealtimeCall, RealtimeEvent } from './realtime-call';

export type { RealtimeSession } from './realtime-call';

/**
 * A voice call on the core's pipeline of the user's models: the page listens live (a WebSocket to
 * the listening model, opened with short-lived credentials: Voxtral Realtime, which also tells
 * when the user's turn starts and ends), a fast text model answers each turn (what to say, and
 * whether to delegate the request to the client), and a speech model says it, sentence by
 * sentence. Its events are told as a GPT-Live call's (`session.started`, transcripts as timed
 * fragments, `session.delegation.created`), and it takes the same appends: words to say
 * (`commentary`, said in the answering model's words), facts (`thinking`) and instructions. Unlike
 * a LiveKit agent, it says what it is given whenever it is given it.
 */

/** A live transcription the page opens (`inference.v1` `transcription.realtime`). */
export type RealtimeListening = {
  protocol: 'mistral';
  url: string;
  protocols: string[];
  model: string;
};
type ModelChoice = { provider: string; model: string };
/** What the core answers a voice call on its pipeline with (`call.create`, Settings › Voice). */
export type RealtimePipelineAnswer = {
  transport: 'pipeline';
  sessionId: null;
  listen: RealtimeListening;
  /** The model answering each turn; null for a call that only listens (dictation). */
  think: ModelChoice | null;
  /** What says the answers (the user's default speech model when not named); null: nothing. */
  speak: { provider?: string; model?: string; voice?: string } | null;
  instructions?: string;
  input?: z.infer<typeof realtimeInputSchema>[];
  language?: string;
};

/** The user's models, through whoever may call them for the caller. */
export type RealtimePipelineModels = {
  /** A text written once (`inference.v1` `text.generate`), JSON when `outputSchema` says. */
  generate(input: {
    instructions: string;
    input: string;
    outputSchema: Record<string, unknown>;
    provider: string;
    model: string;
    signal: AbortSignal;
  }): Promise<string>;
  /** Text read aloud (`inference.v1` `speech`), as MP3 unless asked for WAV. */
  speak(input: {
    text: string;
    provider?: string;
    model?: string;
    voice?: string;
    format?: 'mp3' | 'wav';
    signal: AbortSignal;
  }): Promise<{ audio: string; mimeType: string }>;
};

/** How long the call may take to start listening. */
const START_TIMEOUT_MS = 15_000;
/** The transcription's delay: Mistral's suggested balance of speed and accuracy. */
const STREAMING_DELAY_MS = 480;
/** Once a turn ended, its last words come within this; then it is answered. */
const SETTLE_MS = 250;
const SETTLE_MAX_MS = 900;
/** What the answering model remembers of the call. */
const HISTORY = 40;

/** How the answering model answers: in JSON, what to say and whether to delegate. */
const ANSWERING =
  'You speak in a live voice call: your words are read aloud as they are. Answer in JSON: ' +
  '"say" is what you say now, spoken, short and natural, in the language the user speaks, with no markup; ' +
  '"delegate" is true when the request is one you delegate to the client (see above), false otherwise. ' +
  'When you delegate, "say" only acknowledges it in a few words; never make up a result. ' +
  'When you are told news to give the user, "say" gives it in your own words, briefly, and "delegate" is false.';
const ANSWER_SCHEMA = {
  type: 'object',
  properties: {
    say: { type: 'string' },
    delegate: { type: 'boolean' },
  },
  required: ['say', 'delegate'],
  additionalProperties: false,
};

/** Converts the microphone to 16-bit PCM, posted every tenth of a second. */
const PCM_WORKLET = `
class CrowdexPcm extends AudioWorkletProcessor {
  constructor() { super(); this.parts = []; this.size = 0; this.chunk = Math.round(sampleRate / 10); }
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel) {
      const part = new Int16Array(channel.length);
      for (let i = 0; i < channel.length; i++) {
        const s = Math.max(-1, Math.min(1, channel[i]));
        part[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      this.parts.push(part);
      this.size += part.length;
      if (this.size >= this.chunk) {
        const all = new Int16Array(this.size);
        let at = 0;
        for (const p of this.parts) { all.set(p, at); at += p.length; }
        this.parts = []; this.size = 0;
        this.port.postMessage(all.buffer, [all.buffer]);
      }
    }
    return true;
  }
}
registerProcessor('crowdex-pcm', CrowdexPcm);
`;

export function base64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let at = 0; at < bytes.length; at += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
  return btoa(binary);
}

export function bytes(text: string): ArrayBuffer {
  const binary = atob(text);
  const out = new Uint8Array(binary.length);
  for (let at = 0; at < binary.length; at++) out[at] = binary.charCodeAt(at);
  return out.buffer;
}

/** A reply cut into sentences, each said as soon as it is ready. */
export function spokenSentences(text: string): string[] {
  return text
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?…])\s+(?=\S)/)
    .map((sentence) => sentence.trim())
    .filter((sentence) => sentence !== '');
}

/** The answering model's answer (`{ say, delegate }`); its text as what to say otherwise. */
export function pipelineAnswer(text: string): { say: string; delegate: boolean } {
  try {
    const value = JSON.parse(text) as { say?: unknown; delegate?: unknown };
    return {
      say: typeof value.say === 'string' ? value.say.trim() : '',
      delegate: value.delegate === true,
    };
  } catch {
    return { say: text.trim(), delegate: false };
  }
}

type Line = { role: 'user' | 'assistant' | 'context'; text: string };

/** What the answering model is given: the call so far, then what it answers now. */
export function pipelinePrompt(lines: readonly Line[], now: string): string {
  const said = lines.map((line) =>
    line.role === 'user'
      ? `User: ${line.text}`
      : line.role === 'assistant'
        ? `You: ${line.text}`
        : `(Context, not said: ${line.text})`,
  );
  return `${said.length ? `The call so far:\n${said.join('\n')}\n\n` : ''}${now}`;
}

/** A sentence's audio, ready to play where the call's voice is heard. */
export type PipelineClip = { durationMs: number; play(): Promise<void> };

/** Where the pipeline's voice is heard: the page's speakers, or a room's track. */
export type PipelineSpeaker = {
  /** The audio of a sentence, fetched (and decoded) ahead of its turn. */
  clip(text: string, signal: AbortSignal): Promise<PipelineClip>;
  /** Stops what plays now. */
  stop(): void;
  /** Whether it plays something now. */
  readonly playing: boolean;
};

/** A pipeline call running: its call, and where its microphone's PCM goes. */
export type PipelineRun = {
  call: RealtimeCall;
  /** 16-bit PCM of the user's microphone, at the rate the pipeline listens at. */
  append(pcm: ArrayBuffer): void;
};

/**
 * Runs a call on the core's pipeline, wherever its audio is: listens with the answer's live
 * transcription (16-bit PCM at `sampleRate` given to `append`), answers each turn with the fast
 * model, and says it with `speaker`, sentence by sentence; stops speaking when the user speaks
 * over it. Its events are told as a GPT-Live call's (`emit`).
 */
export async function startPipeline(options: {
  answer: RealtimePipelineAnswer;
  models?: RealtimePipelineModels;
  speaker: PipelineSpeaker;
  sampleRate: number;
  signal?: AbortSignal;
  emit?: (event: RealtimeEvent) => void;
  /** Releases what the call holds of its own (its audio graph). */
  release?(): void;
}): Promise<PipelineRun> {
  const { answer, models, speaker } = options;
  const startedAt = performance.now();
  const now = () => Math.round(performance.now() - startedAt);
  const emit = (event: RealtimeEvent) => options.emit?.(event);
  const history: Line[] = [];
  const remember = (line: Line) => {
    history.push(line);
    history.splice(0, Math.max(0, history.length - HISTORY));
  };
  const instructions = [
    answer.instructions,
    ...(answer.input ?? [])
      .filter((message) => message.role === 'developer')
      .map((message) => message.text),
  ];
  for (const message of answer.input ?? [])
    if (message.role !== 'developer') remember({ role: message.role, text: message.text });
  let ws: WebSocket | null = null;
  let listening = false;
  let muted = false;
  let ended = false;
  let closing: Promise<void> | null = null;

  // Speaking: sentences in turn, all stopped at once when the user speaks over them.
  let generation = 0;
  let queue = Promise.resolve();
  const speakers = new Set<AbortController>();
  const stopSpeaking = () => {
    generation++;
    for (const controller of speakers) controller.abort();
    speakers.clear();
    speaker.stop();
  };
  const play = (clip: PipelineClip, text: string) => {
    const startMs = now();
    emit({
      type: 'session.output_transcript.delta',
      delta: `${text} `,
      start_ms: startMs,
      end_ms: startMs + clip.durationMs,
    });
    return clip.play();
  };
  const say = (text: string) => {
    if (models === undefined || answer.speak === null || ended) return;
    const sentences = spokenSentences(text);
    if (sentences.length === 0) return;
    const turn = generation;
    const controller = new AbortController();
    speakers.add(controller);
    queue = queue.then(async () => {
      const clips: Promise<PipelineClip>[] = [];
      const fetch = (index: number) => {
        const sentence = sentences[index];
        if (sentence !== undefined && clips[index] === undefined) {
          clips[index] = speaker.clip(sentence, controller.signal);
          clips[index].catch(() => undefined);
        }
      };
      fetch(0);
      for (const [index, sentence] of sentences.entries()) {
        if (turn !== generation || ended) break;
        fetch(index + 1);
        try {
          const clip = await clips[index];
          if (clip === undefined || turn !== generation || ended) break;
          await play(clip, sentence);
        } catch (error) {
          if (!controller.signal.aborted)
            emit({
              type: 'pipeline.error',
              message: error instanceof Error ? error.message : String(error),
            });
          break;
        }
      }
      speakers.delete(controller);
    });
  };

  // Answering: each turn of the user, once its words are in; then what the client tells.
  let thinking: AbortController | null = null;
  let telling = Promise.resolve();
  const ask = async (now: string, signal: AbortSignal, lines: readonly Line[] = history) => {
    if (models === undefined || answer.think === null) return null;
    const text = await models.generate({
      instructions: [...instructions, ANSWERING].filter(Boolean).join('\n\n'),
      input: pipelinePrompt(lines, now),
      outputSchema: ANSWER_SCHEMA,
      provider: answer.think.provider,
      model: answer.think.model,
      signal,
    });
    return signal.aborted ? null : pipelineAnswer(text);
  };
  /** A draft answer started as the turn probably ended, kept when nothing more was said. */
  let draft: { text: string; reply: Promise<{ say: string; delegate: boolean } | null> } | null =
    null;
  let draftController: AbortController | null = null;
  const discardDraft = () => {
    draftController?.abort();
    draftController = null;
    draft = null;
  };
  const TURN_PROMPT = "Answer the user's last words.";
  const reply = async (text: string) => {
    thinking?.abort();
    const controller = new AbortController();
    thinking = controller;
    remember({ role: 'user', text });
    try {
      const kept = draft?.text === text ? draft.reply : null;
      draft = null;
      draftController = null;
      const result = await (kept ?? ask(TURN_PROMPT, controller.signal));
      if (result === null || controller.signal.aborted || ended) return;
      if (result.delegate)
        emit({
          type: 'session.delegation.created',
          offset_ms: now(),
          delegation: {
            id: `pipeline_${crypto.randomUUID().replaceAll('-', '')}`,
            target: 'client',
          },
        });
      if (result.say) {
        remember({ role: 'assistant', text: result.say });
        say(result.say);
      }
    } catch (error) {
      if (!controller.signal.aborted)
        emit({
          type: 'pipeline.error',
          message: error instanceof Error ? error.message : String(error),
        });
    } finally {
      if (thinking === controller) thinking = null;
    }
  };
  /** Words the client gives to say: in the answering model's words, in turn. */
  const tell = (text: string) => {
    telling = telling.then(async () => {
      if (ended) return;
      remember({ role: 'context', text: `The assistant gives this to tell the user: ${text}` });
      const controller = new AbortController();
      const result =
        models === undefined || answer.think === null
          ? null
          : await ask('Tell the user what the assistant gave you, now.', controller.signal).catch(
              () => null,
            );
      const said = result?.say || text;
      remember({ role: 'assistant', text: said });
      say(said);
    });
  };

  // The user's turn: its words as they come, answered once it ended.
  let heard = '';
  let turnStartMs: number | null = null;
  let lastWordAt = 0;
  let ending: ReturnType<typeof setTimeout> | null = null;
  const endTurn = (waited = 0) => {
    if (ending !== null) clearTimeout(ending);
    // Its last words may come a little after the turn ended.
    if (waited < SETTLE_MAX_MS && performance.now() - lastWordAt < SETTLE_MS) {
      ending = setTimeout(() => endTurn(waited + SETTLE_MS / 2), SETTLE_MS / 2);
      return;
    }
    ending = null;
    turnStartMs = null;
    const text = heard.replace(/\s+/g, ' ').trim();
    heard = '';
    if (text) void reply(text);
  };
  const onMessage = (data: unknown) => {
    if (typeof data !== 'string') return;
    let event: Record<string, unknown>;
    try {
      event = JSON.parse(data) as Record<string, unknown>;
    } catch {
      return;
    }
    switch (event['type']) {
      case 'transcription.text.delta': {
        const text = event['text'];
        if (typeof text !== 'string' || !text) return;
        heard += text;
        lastWordAt = performance.now();
        // Speaking over the voice stops it (words, not a breath).
        if (speaker.playing && /\p{L}/u.test(text)) stopSpeaking();
        emit({
          type: 'session.input_transcript.delta',
          delta: text,
          start_ms: turnStartMs ?? now(),
          end_ms: now(),
        });
        return;
      }
      case 'turn.start':
        turnStartMs = now();
        return;
      case 'turn.eager_end': {
        const text = heard.replace(/\s+/g, ' ').trim();
        if (!text || models === undefined || answer.think === null) return;
        discardDraft();
        const controller = new AbortController();
        draftController = controller;
        // Given the call as it will be once this turn is remembered.
        draft = {
          text,
          reply: ask(TURN_PROMPT, controller.signal, [...history, { role: 'user', text }]).catch(
            () => null,
          ),
        };
        return;
      }
      case 'turn.resume':
        discardDraft();
        return;
      case 'turn.end':
        endTurn();
        return;
      case 'error':
        emit({ type: 'pipeline.error', message: JSON.stringify(event['error'] ?? event) });
        return;
      default:
        return;
    }
  };

  const teardown = () => {
    if (ended) return;
    ended = true;
    stopSpeaking();
    thinking?.abort();
    discardDraft();
    if (ending !== null) clearTimeout(ending);
    try {
      ws?.close();
    } catch {}
    options.release?.();
  };
  const close = () => {
    if (closing !== null) return closing;
    closing = (async () => {
      if (!ended && listening && ws?.readyState === WebSocket.OPEN)
        try {
          ws.send(JSON.stringify({ type: 'input_audio.end' }));
        } catch {}
      teardown();
      emit({ type: 'session.closed' });
    })();
    return closing;
  };
  const send = (event: RealtimeEvent) => {
    if (ended) return false;
    switch (event.type) {
      case 'session.close':
        void close();
        return true;
      case 'session.input_audio.mute':
      case 'session.input_audio.unmute':
        muted = event.type === 'session.input_audio.mute';
        return true;
      case 'session.thinking.append':
      case 'session.instructions.append':
      case 'session.commentary.append': {
        const content = event['content'];
        if (typeof content !== 'string') return false;
        if (event.type === 'session.instructions.append') instructions.push(content);
        else if (event.type === 'session.thinking.append')
          remember({ role: 'context', text: content });
        else appended(content);
        return true;
      }
      default:
        return true;
    }
  };
  // An append comes in parts, in one turn of the call: said once whole.
  let parts: string[] | null = null;
  const appended = (content: string) => {
    if (parts !== null) {
      parts.push(content);
      return;
    }
    parts = [content];
    setTimeout(() => {
      const text = (parts ?? []).reduce(
        (all, part) =>
          all === '' || /\s$/.test(all) || /^\s/.test(part) ? all + part : `${all} ${part}`,
        '',
      );
      parts = null;
      tell(text);
    }, 0);
  };

  const aborted = () => teardown();
  options.signal?.addEventListener('abort', aborted, { once: true });
  try {
    if (answer.listen.protocol !== 'mistral')
      throw new Error(`The voice call cannot listen with ${String(answer.listen.protocol)}.`);
    const socket = new WebSocket(answer.listen.url, answer.listen.protocols);
    ws = socket;
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(
        () => fail(new Error('The voice call did not start listening.')),
        START_TIMEOUT_MS,
      );
      const fail = (error: Error) => {
        clearTimeout(timeout);
        reject(error);
      };
      socket.addEventListener('error', () => fail(new Error('The voice call could not listen.')));
      socket.addEventListener('close', () => fail(new Error('The voice call stopped listening.')));
      options.signal?.addEventListener(
        'abort',
        () => fail(new DOMException('Cancelled', 'AbortError')),
        { once: true },
      );
      socket.addEventListener('message', ({ data }) => {
        if (listening) return onMessage(data);
        let event: { type?: unknown; error?: { message?: unknown } } = {};
        try {
          event = JSON.parse(String(data)) as typeof event;
        } catch {}
        if (event.type === 'error')
          return fail(
            new Error(String(event.error?.message ?? 'The voice call could not listen.')),
          );
        // Configured before any audio: it cannot change afterwards.
        if (event.type === 'session.created')
          socket.send(
            JSON.stringify({
              type: 'session.update',
              session: {
                audio_format: { encoding: 'pcm_s16le', sample_rate: options.sampleRate },
                target_streaming_delay_ms: STREAMING_DELAY_MS,
                ...(answer.language === undefined ? {} : { language: answer.language }),
                turn_detection: {},
              },
            }),
          );
        if (event.type === 'session.updated') {
          clearTimeout(timeout);
          listening = true;
          resolve();
        }
      });
    });
    if (options.signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    emit({ type: 'session.started' });
    return {
      call: { sessionId: null, transport: 'pipeline', protocol: 'v1', send, close },
      append(pcm) {
        if (muted || ended || ws?.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify({ type: 'input_audio.append', audio: base64(pcm) }));
      },
    };
  } catch (error) {
    teardown();
    throw error;
  } finally {
    options.signal?.removeEventListener('abort', aborted);
  }
}

/**
 * A pipeline call in the page (dictation listens with it): the microphone's PCM from an audio
 * worklet, the voice played on the page's speakers (`onRemoteStream`).
 */
export async function joinPipelineCall(options: {
  answer: RealtimePipelineAnswer;
  stream: MediaStream;
  models?: RealtimePipelineModels;
  /** Releases what the call holds of its own (a silent stand-in microphone). */
  release(): void;
  signal?: AbortSignal;
  onEvent?: (event: RealtimeEvent) => void;
  onRemoteStream?: (stream: MediaStream) => void;
}): Promise<RealtimeCall> {
  const { answer, models } = options;
  const context = new AudioContext();
  void context.resume().catch(() => undefined);
  const output = context.createMediaStreamDestination();
  const playing = new Set<AudioBufferSourceNode>();
  let source: MediaStreamAudioSourceNode | null = null;
  let worklet: AudioWorkletNode | null = null;
  let workletUrl: string | null = null;
  const speaker: PipelineSpeaker = {
    get playing() {
      return playing.size > 0;
    },
    stop() {
      for (const node of playing)
        try {
          node.stop();
        } catch {}
      playing.clear();
    },
    async clip(text, signal) {
      const spoken = await (models as RealtimePipelineModels).speak({
        text,
        ...(answer.speak?.provider === undefined ? {} : { provider: answer.speak.provider }),
        ...(answer.speak?.model === undefined ? {} : { model: answer.speak.model }),
        ...(answer.speak?.voice === undefined ? {} : { voice: answer.speak.voice }),
        signal,
      });
      const buffer = await context.decodeAudioData(bytes(spoken.audio));
      return {
        durationMs: Math.round(buffer.duration * 1000),
        play: () =>
          new Promise<void>((resolve) => {
            const node = context.createBufferSource();
            node.buffer = buffer;
            node.connect(output);
            playing.add(node);
            node.addEventListener('ended', () => {
              playing.delete(node);
              resolve();
            });
            node.start();
          }),
      };
    },
  };
  try {
    const run = await startPipeline({
      answer,
      ...(models === undefined ? {} : { models }),
      speaker,
      sampleRate: context.sampleRate,
      ...(options.signal === undefined ? {} : { signal: options.signal }),
      ...(options.onEvent === undefined ? {} : { emit: options.onEvent }),
      release: () => {
        source?.disconnect();
        worklet?.disconnect();
        if (workletUrl !== null) URL.revokeObjectURL(workletUrl);
        void context.close().catch(() => undefined);
        options.release();
      },
    });
    workletUrl = URL.createObjectURL(new Blob([PCM_WORKLET], { type: 'application/javascript' }));
    await context.audioWorklet.addModule(workletUrl);
    source = context.createMediaStreamSource(options.stream);
    worklet = new AudioWorkletNode(context, 'crowdex-pcm');
    worklet.port.onmessage = ({ data }: MessageEvent<ArrayBuffer>) => run.append(data);
    // Pulled by the graph, silently.
    const silence = context.createGain();
    silence.gain.value = 0;
    source.connect(worklet);
    worklet.connect(silence);
    silence.connect(context.destination);
    options.onRemoteStream?.(output.stream);
    return run.call;
  } catch (error) {
    void context.close().catch(() => undefined);
    throw error;
  }
}
